// Extraction mécanique de client.ts (WO-V3-02 / US-019) — méthodes API du domaine.
import { request, ApiError, httpMessage, BASE } from './http'
import { getToken } from '../auth/session'
import type { DocumentSummary, GitLabBranch, GitLabJob, GitLabMergeRequest, GitLabMrResult, GitLabPipeline, GitLabProject, ItemAttachment, ScmConnection, ScmItemProvenance } from './types/scmAudit'

export const scmAndAttachmentsApi = {
  // ---- Connecteur SCM/CI GitLab (Phase 4) -------------------------------
  getScmConnection: (workspaceId: string) =>
    request<ScmConnection>(`/scm/gitlab?workspace_id=${encodeURIComponent(workspaceId)}`),

  /** Enregistre la connexion ; jeton omis = inchangé (write-only côté serveur). */
  saveScmConnection: (workspaceId: string,
                      cfg: { base_url: string; project_id: string; token?: string }) =>
    request<ScmConnection>(`/scm/gitlab?workspace_id=${encodeURIComponent(workspaceId)}`, {
      method: 'PUT',
      body: JSON.stringify(
        cfg.token ? cfg : { base_url: cfg.base_url, project_id: cfg.project_id }),
    }),

  deleteScmConnection: (workspaceId: string) =>
    request<{ deleted: boolean }>(
      `/scm/gitlab?workspace_id=${encodeURIComponent(workspaceId)}`, { method: 'DELETE' }),

  testScmConnection: (workspaceId: string) =>
    request<GitLabProject>(`/scm/gitlab/test?workspace_id=${encodeURIComponent(workspaceId)}`),

  scmPipelines: (workspaceId: string) =>
    request<GitLabPipeline[]>(
      `/scm/gitlab/pipelines?workspace_id=${encodeURIComponent(workspaceId)}`),

  triggerScmPipeline: (workspaceId: string, ref: string) =>
    request<GitLabPipeline>(`/scm/gitlab/pipeline?workspace_id=${encodeURIComponent(workspaceId)}`,
      { method: 'POST', body: JSON.stringify({ ref }) }),

  scmMergeRequests: (workspaceId: string) =>
    request<GitLabMergeRequest[]>(
      `/scm/gitlab/merge-requests?workspace_id=${encodeURIComponent(workspaceId)}`),

  scmBranches: (workspaceId: string) =>
    request<GitLabBranch[]>(`/scm/gitlab/branches?workspace_id=${encodeURIComponent(workspaceId)}`),

  scmItemProvenance: (workspaceId: string, itemId: number) =>
    request<ScmItemProvenance>(
      `/scm/gitlab/item-provenance?workspace_id=${encodeURIComponent(workspaceId)}&item_id=${itemId}`),

  scmPipelineJobs: (workspaceId: string, pipelineId: number) =>
    request<GitLabJob[]>(`/scm/gitlab/pipeline-jobs?workspace_id=${encodeURIComponent(workspaceId)}&pipeline_id=${pipelineId}`),

  scmJobTrace: (workspaceId: string, jobId: number) =>
    request<string>(`/scm/gitlab/job-trace?workspace_id=${encodeURIComponent(workspaceId)}&job_id=${jobId}`),

  /** ÉCRITURE : propose une story sur GitLab (branche + commit + MR). Idempotent. */
  proposeGitlabMr: (workspaceId: string, itemId: number) =>
    request<GitLabMrResult>(
      `/scm/gitlab/merge-request?workspace_id=${encodeURIComponent(workspaceId)}`,
      { method: 'POST', body: JSON.stringify({ item_id: itemId }) }),

  // ---- Pièces jointes d'items (Phase 4) ---------------------------------
  listItemAttachments: (itemId: number) =>
    request<ItemAttachment[]>(`/backlog/${itemId}/attachments`),

  /** Téléverse une pièce jointe (multipart, comme `uploadDocument`). */
  uploadItemAttachment: async (itemId: number, file: File): Promise<ItemAttachment> => {
    const form = new FormData()
    form.append('file', file)
    const token = getToken()
    const response = await fetch(`${BASE}/backlog/${itemId}/attachments`, {
      method: 'POST',
      body: form,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    if (!response.ok) {
      throw new ApiError(response.status, httpMessage(response.status),
        await response.text().catch(() => undefined))
    }
    return (await response.json()) as ItemAttachment
  },

  deleteItemAttachment: (id: number) =>
    request<{ id: number; deleted: boolean }>(`/backlog/attachments/${id}`, { method: 'DELETE' }),

  /** Télécharge une pièce jointe (binaire authentifié) et déclenche l'enregistrement navigateur. */
  downloadItemAttachment: async (id: number, filename: string): Promise<void> => {
    const token = getToken()
    const response = await fetch(`${BASE}/backlog/attachments/${id}/download`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    if (!response.ok) {
      throw new ApiError(response.status, httpMessage(response.status),
        await response.text().catch(() => undefined))
    }
    const blob = await response.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  },

  /** L'upload n'est pas du JSON : on contourne `request` en conservant sa gestion d'erreur. */
  uploadDocument: async (
    workspaceId: string,
    file: File,
    onProgress?: (percent: number) => void,
  ): Promise<DocumentSummary> => {
    const form = new FormData()
    form.append('file', file)
    form.append('workspace_id', workspaceId)
    const token = getToken()
    // `fetch` ne rend pas la progression d'un corps multipart au navigateur. Lorsque
    // l'écran doit la montrer, XHR est le transport adapté : le pourcentage est celui
    // des octets réellement envoyés, pas une estimation locale.
    if (onProgress) {
      return new Promise<DocumentSummary>((resolve, reject) => {
        const request = new XMLHttpRequest()
        request.open('POST', `${BASE}/documents/upload`)
        if (token) request.setRequestHeader('Authorization', `Bearer ${token}`)
        request.upload.onprogress = (event) => {
          if (event.lengthComputable && event.total > 0) {
            onProgress(Math.round((event.loaded / event.total) * 100))
          }
        }
        request.onerror = () => reject(new ApiError(0, 'Erreur réseau pendant le téléversement'))
        request.onload = () => {
          if (request.status >= 200 && request.status < 300) {
            try {
              resolve(JSON.parse(request.responseText) as DocumentSummary)
            } catch {
              reject(new ApiError(request.status, 'Réponse de téléversement invalide'))
            }
            return
          }
          reject(new ApiError(request.status, httpMessage(request.status), request.responseText || undefined))
        }
        request.send(form)
      })
    }
    const response = await fetch(`${BASE}/documents/upload`, {
      method: 'POST',
      body: form,
      // Pas de Content-Type explicite : le navigateur doit poser lui-même la
      // frontière multipart, qu'une valeur écrite à la main casserait.
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    if (!response.ok) {
      throw new ApiError(response.status, httpMessage(response.status),
        await response.text().catch(() => undefined))
    }
    return (await response.json()) as DocumentSummary
  },
}
